/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { ActivityMessage, type AuditLogEventData } from '@atlassian/audit-logs-common';

import { CopyJsonButton } from '../../../../../common/utils/components/copy-json-button';
import { EventDateTime } from '../../../../../common/utils/components/event-date-time';
import {
	SummarySection as GenericSummarySection,
	type SummarySectionRow,
} from '../../../../../common/utils/components/summary-section';

const messages = defineMessages({
	summary: {
		id: 'organization.audit.log.side-panel.tab-panels.details.summary-section.title',
		defaultMessage: 'Summary',
		description: 'Title for the summary section in audit log details panel',
	},
	category: {
		id: 'organization.audit.log.side-panel.tab-panels.details.summary-section.category.label',
		defaultMessage: 'Category',
		description: 'Label for the category field in audit log summary section',
	},
	activity: {
		id: 'organization.audit.log.side-panel.tab-panels.details.summary-section.activity.label',
		defaultMessage: 'Activity',
		description: 'Label for the activity field in audit log summary section',
	},
	action: {
		id: 'organization.audit.log.side-panel.tab-panels.details.summary-section.action.label',
		defaultMessage: 'Action',
		description: 'Label for the action field in audit log summary section',
	},
	type: {
		id: 'organization.audit.log.side-panel.tab-panels.details.summary-section.type.label',
		defaultMessage: 'Type',
		description: 'Label for the event type field in audit log summary section',
	},
	activityId: {
		id: 'organization.audit.log.side-panel.tab-panels.details.summary-section.activity-id.label',
		defaultMessage: 'Activity ID',
		description: 'Label for the activity ID field in audit log summary section',
	},
	date: {
		id: 'organization.audit.log.side-panel.tab-panels.details.summary-section.date.label',
		defaultMessage: 'Date',
		description: 'Label for the date/time field in audit log summary section',
	},
	json: {
		id: 'organization.audit.log.side-panel.tab-panels.details.summary-section.json.label',
		defaultMessage: 'JSON',
		description: 'Label for the JSON data field in audit log summary section',
	},
});

interface ActivitySummarySectionProps {
	event: AuditLogEventData;
}

export const SummarySection = ({ event }: ActivitySummarySectionProps): JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!event || !event.details) {
		return null;
	}

	const { activity, action, type, activityId, date, category } = event.details;

	const rows: SummarySectionRow[] = [];

	if (activity && activity.message) {
		rows.push({
			label: formatMessage(messages.activity),
			value: <ActivityMessage activity={activity} />,
		});
	}

	if (category) {
		rows.push({ label: formatMessage(messages.category), value: category });
	}

	rows.push({ label: formatMessage(messages.action), value: action });
	rows.push({ label: formatMessage(messages.type), value: type });
	rows.push({ label: formatMessage(messages.activityId), value: activityId });

	if (date) {
		rows.push({
			label: formatMessage(messages.date),
			value: <EventDateTime timestamp={date} />,
		});
	}

	rows.push({
		label: formatMessage(messages.json),
		value: <CopyJsonButton eventJSON={JSON.stringify(event)} />,
	});

	return <GenericSummarySection title={formatMessage(messages.summary)} rows={rows} />;
};
